import { exportDocumentToDocx } from "./docxExport";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

const NEW_DOCUMENT_NAME = "New Set";
const NEW_DOCUMENT_BODY = "== Bit\nthis is a setup *punchline.\n";

// Starts an unsaved draft. Nothing is written to the server until the user
// saves (see handleSave), so opening the editor and leaving doesn't leave
// empty documents behind. `id` is kept when the editor was opened with an
// id that doesn't exist yet (e.g. today's journal entry) so the first save
// creates the document under that id.
function startDraft(setField, id) {
  setField("id", id);
  setField("isNew", true);
  setField("name", NEW_DOCUMENT_NAME);
  setField("body", NEW_DOCUMENT_BODY);
}

export async function loadDocument({ paramId, auth, setField }) {
  setField("error", "");
  setField("loading", true);
  try {
    if (paramId) {
      const res = await fetch(`${API_BASE_URL}/documents/${paramId}`, {
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
      });
      if (res.ok) {
        const doc = await res.json();
        setField("id", doc.id);
        setField("isNew", false);
        setField("name", doc.name || "");
        setField("body", doc.content || "");
      } else if (res.status === 404) {
        startDraft(setField, paramId);
      } else {
        let errorText = await res.text();
        console.error(
          "Failed to load document:",
          res.status,
          res.statusText,
          errorText,
        );
        setField("error", "Failed to load document");
      }
    } else {
      startDraft(setField, null);
    }
  } catch (err) {
    setField("error", "Failed to load document: " + err.message);
  } finally {
    setField("loading", false);
  }
}

export async function handleSave(e, ctx) {
  if (e && e.preventDefault) e.preventDefault();
  ctx.setSaving(true);
  ctx.setError("");
  ctx.setSuccess("");
  try {
    let res;
    const body = {
      name: ctx.name,
      content: ctx.body,
    };

    // Include document_type only if documentType is provided
    if (ctx.documentType) {
      body.document_type = ctx.documentType;
    }

    console.log("handleSave documentType:", ctx.documentType);

    if (ctx.isNew) {
      // First save of a draft: create it (under its intended id, if any)
      res = await fetch(`${API_BASE_URL}/documents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ctx.auth.user?.access_token}`,
        },
        body: JSON.stringify(ctx.id ? { ...body, id: ctx.id } : body),
      });
    } else if (ctx.id) {
      res = await fetch(`${API_BASE_URL}/documents/${ctx.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ctx.auth.user?.access_token}`,
        },
        body: JSON.stringify(body),
      });
    } else {
      ctx.setError("No document loaded to save.");
      ctx.setSaving(false);
      return;
    }
    if (res.ok) {
      const data = await res.json();
      if (ctx.isNew) ctx.onCreated(data.id);
      ctx.setSuccess("Document saved!");
      // Return new revision ID if present
      if (data.newRevisionId) {
        return { newRevisionId: data.newRevisionId };
      }
    } else {
      const data = await res.json();
      ctx.setError(
        data.error
          ? `Error: ${data.error}`
          : `Failed to save document (HTTP ${res.status}: ${res.statusText})`,
      );
    }
  } catch (err) {
    ctx.setError("Failed to save document: " + err.message);
  }
  ctx.setSaving(false);
}

export async function handleFormat(ctx) {
  ctx.setError("");
  ctx.setSuccess("");
  ctx.setFormatting(true);
  try {
    const res = await fetch(`${API_BASE_URL}/ai/format`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ctx.auth.user?.access_token}`,
      },
      body: JSON.stringify({ text: ctx.body }),
    });
    if (res.ok) {
      const data = await res.json();
      ctx.setBody(data.formatted || "");
      ctx.setSuccess("Formatted!");
    } else {
      const data = await res.json();
      ctx.setError(
        data.error
          ? `Format error: ${data.error}`
          : `Failed to format (HTTP ${res.status}: ${res.statusText})`,
      );
    }
  } catch (err) {
    ctx.setError("Failed to format: " + err.message);
  }
  ctx.setFormatting(false);
}

export async function handleAddBits(ctx) {
  ctx.setError("");
  ctx.setSuccess("");
  ctx.setFormatting(true);
  try {
    const res = await fetch(`${API_BASE_URL}/ai/add_bits`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ctx.auth.user?.access_token}`,
      },
      body: JSON.stringify({ text: ctx.body }),
    });
    if (res.ok) {
      const data = await res.json();
      ctx.setBody(data.bits || "");
      ctx.setSuccess("Bits added!");
    } else {
      const data = await res.json();
      ctx.setError(
        data.error
          ? `Add Bits error: ${data.error}`
          : `Failed to add bits (HTTP ${res.status}: ${res.statusText})`,
      );
    }
  } catch (err) {
    ctx.setError("Failed to add bits: " + err.message);
  }
  ctx.setFormatting(false);
}

export async function handleCoachReview(ctx) {
  ctx.setError("");
  ctx.setReviewing(true);
  try {
    const res = await fetch(`${API_BASE_URL}/ai/coach_review`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ctx.auth.user?.access_token}`,
      },
      body: JSON.stringify({ text: ctx.body }),
    });
    if (res.ok) {
      const data = await res.json();
      ctx.setCoachReview(data);
      ctx.setShowCoachReview(true);
    } else {
      const data = await res.json();
      ctx.setError(
        data.error
          ? `Grr Review error: ${data.error}`
          : `Failed to get Grr Review (HTTP ${res.status}: ${res.statusText})`,
      );
    }
  } catch (err) {
    ctx.setError("Failed to get Grr Review: " + err.message);
  }
  ctx.setReviewing(false);
}

export async function handleDelete(ctx) {
  // A draft was never saved, so there's nothing on the server to delete
  if (ctx.isNew) {
    ctx.setConfirmDelete(false);
    ctx.navigate("/");
    return;
  }
  try {
    const res = await fetch(`${API_BASE_URL}/documents/${ctx.id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${ctx.auth.user?.access_token}`,
      },
    });
    if (res.ok) {
      ctx.navigate("/");
    } else {
      ctx.setError("Failed to delete document");
    }
  } catch (err) {
    ctx.setError("Failed to delete document: " + err.message);
  }
  ctx.setConfirmDelete(false);
}

export async function handleExportDocx(
  ctx,
  { includeHeaders = true, includeTiming = true } = {}
) {
  ctx.setError("");
  ctx.setSuccess("");
  try {
    await exportDocumentToDocx({
      name: ctx.name,
      body: ctx.body,
      wordsPerMinute: ctx.wordsPerMinute,
      includeHeaders,
      includeTiming,
    });
    ctx.setSuccess("Exported to Docx!");
  } catch (err) {
    ctx.setError("Failed to export: " + err.message);
  }
}

export function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 600px)").matches;
}

export async function handleCustomPrompt({ prompt, contextText, documentText, auth }) {
  // Returns { answer, error }
  try {
    const res = await fetch(`${API_BASE_URL}/ai/custom_prompt`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.user?.access_token}`,
      },
      body: JSON.stringify({
        prompt,
        text: documentText + "\n Current conversation:\n" + contextText, 
      }),
    });
    if (res.ok) {
      const data = await res.json();
      return { answer: data.answer || "" };
    } else {
      const data = await res.json();
      return { error: data.error || "Custom prompt failed" };
    }
  } catch (err) {
    return { error: "Custom prompt failed: " + err.message };
  }
}
